<script setup lang="ts">
import { useAddToCartFlow } from "~~/app/composables/useAddToCartFlow";
import { useNotifications } from "~~/app/composables/useNotifications";
import { useQuickView } from "~~/app/composables/useQuickView";
import { useAnalytics } from "~~/app/composables/useAnalytics";
import type { Product, SizeVariant } from "~~/shared/types/store";

const props = defineProps<{
  product: Product;
  loading?: boolean;
  imageOnly?: boolean;
  eager?: boolean;
  itemListId?: string;
  itemListName?: string;
  index?: number;
  // Responsywny `sizes` dla NuxtImg — domyślnie siatka listingu (2 kol. mobile → 4 kol. desktop).
  // Sekcje z dużymi kaflami (editorial) przekazują większą wartość, by uniknąć rozmycia.
  imageSizes?: string;
}>();

// Domyślny `sizes` dla siatki listingu; nadpisywany propem dla dużych kafli.
// Mobile: siatka jest 2-kolumnowa, więc karta zajmuje ~50vw, NIE 100vw.
//
// KAŻDY token MUSI mieć klucz breakpointu (xs/sm/md/lg/…). @nuxt/image v2.0.0
// parseSizes traktuje token BEZ prefiksu (np. "50vw") jak klucz "1px":
// screens["1px"]=undefined → parseInt("1px")=1 → _cWidth=round(50/100·1)=1 →
// srcset dostaje wariant s_1x1 (1 px!). Na mobile (≤md) to jedyny kandydat pasujący
// do tokena mobilnego, więc przeglądarka spada do przeładowanego fallbacku `src`
// (s_506x674) zamiast dobrać ~200–400 px. To była główna przyczyna wagi obrazów
// listingu na mobile (raport PSI: ~460–630 KiB nadmiaru). Ta sama regresja co hero
// na home (6698b52). xs/sm=50vw (2 kol. mobile), md 33vw, lg+ 25vw (jak wcześniej,
// bez zmiany desktopu); dodane xl/xxl/3xl zagęszczają pulę kandydatów, by mobile
// mogło wybrać ~384 px zamiast przeskoczyć do 506 px.
const resolvedImageSizes = computed(
  () =>
    props.imageSizes ??
    "xs:50vw sm:50vw md:33vw lg:25vw xl:25vw xxl:25vw 3xl:25vw",
);

// Miniatury listingu (wyświetlane ~188 px mobile / ~413 px desktop) nie potrzebują
// globalnej jakości 95 (ustawionej dla lightboxa PDP, gdzie liczy się ostrość
// oryginału). q75 WebP jest wizualnie nieodróżnialne w tej skali, a ścina ~40 %
// wagi — mniej rywalizacji o pasmo 4G z banerem hero (element LCP) na mobile.
// Strojenie: podnieś, jeśli klient zauważy zmiękczenie na fakturze tkanin.
const CARD_IMAGE_QUALITY = 75;

const { formatPrice } = useMoney();
// „+ VAT 23%" przy cenie (BrandConfig.priceTaxNote) — ta sama zasada co na karcie
// produktu; marki bez dopisku (lavmag, youlook) bez zmian.
const priceTaxNote = useBrand().priceTaxNote ?? "";
// Tryb katalogowy: w miejscu przycisku zakupu stoi „Zapytaj o wycenę",
// które otwiera modal z formularzem. Świadomie przycisk, nie link:
// `@click.prevent` NuxtLinka nie powstrzymuje (routuje własnym handlerem),
// więc modal otwierał się i ginął razem z przejściem na /contact.
const { sellingEnabled, ctaLabel } = useCatalogOnly();
const { open: openQuote } = useQuoteRequest();
const addToCartFlow = useAddToCartFlow();
const notifications = useNotifications();
const quickView = useQuickView();
const analytics = useAnalytics();
const instantProductNavigation = useInstantProductNavigation();
const imageRoot = ref<HTMLElement | null>(null);
const lazyImageRootMargin = "800px 0px";
const shouldLoadImages = ref(props.eager === true);
let imageObserver: IntersectionObserver | null = null;

const imageLoading = computed(() => (props.eager ? "eager" : "lazy"));
const imageFetchPriority = computed(() => (props.eager ? "high" : "low"));

const discountPercent = computed(() => {
  if (!props.product.compareAtPrice || !props.product.price) return null;
  const pct = Math.round(
    (1 - props.product.price / props.product.compareAtPrice) * 100,
  );
  return pct > 0 ? `-${pct}%` : null;
});

const isNew = computed(() => props.product.badges?.includes("Nowość"));

// Link do listingu marki — ten sam wzorzec co na PDP (`/marka/{id}-{slug}`).
const brandLink = computed(() =>
  props.product.brand && props.product.brandId && props.product.brandSlug
    ? `/marka/${props.product.brandId}-${props.product.brandSlug}`
    : null,
);

const cardLink = computed(() => {
  const variants = props.product.sizeVariants ?? [];
  const currentVariant =
    variants.find((variant) => variant.isCurrent) ??
    variants.find((variant) => variant.available) ??
    variants[0] ??
    null;

  if (currentVariant?.slug) {
    return `/products/${currentVariant.id}-${currentVariant.slug}`;
  }
  if (props.product.sizeVariants?.length) {
    const v = props.product.sizeVariants[0];
    return `/products/${v.id}-${v.slug}`;
  }
  return `/products/${props.product.id}-${props.product.slug}`;
});

const quickViewSlug = computed(() => {
  const variants = props.product.sizeVariants ?? [];
  const current =
    variants.find((v) => v.isCurrent) ??
    variants.find((v) => v.available) ??
    variants[0];
  if (current?.id && current?.slug) return `${current.id}-${current.slug}`;
  return `${props.product.id}-${props.product.slug}`;
});

const resolvedProductId = computed(() => {
  const variants = props.product.sizeVariants ?? [];
  const current =
    variants.find((v) => v.isCurrent) ??
    variants.find((v) => v.available) ??
    null;
  const raw = current?.id ?? props.product.id;
  const n = typeof raw === "number" ? raw : Number(raw);
  return Number.isInteger(n) && n > 0 ? n : null;
});

const atStockLimit = computed(() => {
  const id = resolvedProductId.value;
  if (!id) return false;
  return (
    addToCartFlow.isAtStockLimit?.({ productId: id, productAttributeId: 0 }) ??
    false
  );
});

function variantAtLimit(variant: SizeVariant): boolean {
  const id = typeof variant.id === "number" ? variant.id : Number(variant.id);
  if (!Number.isInteger(id) || id <= 0) return false;
  return (
    addToCartFlow.isAtStockLimit?.({ productId: id, productAttributeId: 0 }) ??
    false
  );
}

// Produkt z więcej niż jednym rozmiarem — klik otwiera modal wyboru.
const isMultiSize = computed(
  () =>
    (props.product.sizeVariants?.length ?? 0) > 1 ||
    (props.product.sizes?.length ?? 0) > 1,
);

// Czy WSZYSTKIE znane warianty są niedostępne lub osiągnęły limit w koszyku.
// Liczone tylko gdy karta zna warianty — listing czasem ich nie niesie
// (wtedy stan rozmiarów rozstrzyga modal po dociągnięciu).
const allVariantsExhausted = computed(() => {
  const variants = props.product.sizeVariants ?? [];
  if (variants.length === 0) return false;
  return variants.every((v) => !v.available || variantAtLimit(v));
});

// Powód zablokowania przycisku „Dodaj do koszyka" z tytułu stanu magazynowego.
// Jednorozmiarowy: limit jego jedynego wariantu (zachowanie bez zmian).
// Wielorozmiarowy: blokuje DOPIERO gdy żaden rozmiar nie da się dodać — dopóki
// choć jeden jest dostępny, przycisk pozostaje aktywny i otwiera modal, w którym
// szary jest tylko konkretny, wyczerpany rozmiar.
const stockBlocked = computed(() =>
  isMultiSize.value ? allVariantsExhausted.value : atStockLimit.value,
);

function stopImageObserver() {
  imageObserver?.disconnect();
  imageObserver = null;
}

function loadImages() {
  shouldLoadImages.value = true;
  stopImageObserver();
}

watch(
  () => props.eager,
  (eager) => {
    if (eager) loadImages();
  },
);

onMounted(() => {
  if (shouldLoadImages.value) return;
  if (!("IntersectionObserver" in window)) {
    loadImages();
    return;
  }

  imageObserver = new IntersectionObserver(
    (entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        loadImages();
      }
    },
    { rootMargin: lazyImageRootMargin },
  );

  if (imageRoot.value) {
    imageObserver.observe(imageRoot.value);
  } else {
    loadImages();
  }
});

onBeforeUnmount(() => {
  stopImageObserver();
});

async function addToCart() {
  if (!resolvedProductId.value) return;
  try {
    await addToCartFlow.add({
      productId: resolvedProductId.value,
      productAttributeId: 0,
      quantity: 1,
    });
    if (addToCartFlow.stockError.value) {
      notifications.error(addToCartFlow.stockError.value);
    } else {
      analytics.trackAddToCart(props.product, 1, {
        itemListId: props.itemListId,
        itemListName: props.itemListName,
        index: props.index,
      });
    }
  } catch {
    // errors handled by addToCartFlow notifications
  }
}

const isSizePickerOpen = ref(false);
const pickerSourceVariants = ref<SizeVariant[]>([]);
const isResolvingSizes = ref(false);

// Warianty dla modala wzbogacone o `atLimit` (rozmiar już w koszyku na maksymalną
// dostępną ilość). Computed → po dodaniu i ponownym otwarciu modala dany rozmiar
// szarzeje reaktywnie wraz z aktualizacją koszyka.
type PickerVariant = SizeVariant & { atLimit: boolean };
const pickerVariants = computed<PickerVariant[]>(() =>
  pickerSourceVariants.value.map((v) => ({ ...v, atLimit: variantAtLimit(v) })),
);

// Listing nie niesie informacji o rozmiarach (sizeVariants puste na karcie),
// więc po kliknięciu dociągamy pełny produkt; gdy ma >1 rozmiar — pokazujemy
// modal wyboru, w przeciwnym razie dodajemy wprost.
async function onAddToCartClick() {
  if (isResolvingSizes.value || addToCartFlow.isPending.value) return;

  // Jeśli karta już zna warianty (np. dane szczegółowe) — użyj ich bez dociągania.
  const known = props.product.sizeVariants ?? [];
  if (known.length > 1) {
    pickerSourceVariants.value = known;
    isSizePickerOpen.value = true;
    return;
  }

  // Hint z listingu: gdy produkt ma ≤1 etykietę rozmiaru, nie dociągamy —
  // dodajemy wprost (bez zbędnego round-tripu i bez modala).
  if ((props.product.sizes?.length ?? 0) <= 1) {
    await addToCart();
    return;
  }

  isResolvingSizes.value = true;
  try {
    const data = await $fetch<{ product?: { sizeVariants?: SizeVariant[] } }>(
      `/api/store/products/${quickViewSlug.value}`,
    );
    const variants = data?.product?.sizeVariants ?? [];
    if (variants.length > 1) {
      pickerSourceVariants.value = variants;
      isSizePickerOpen.value = true;
    } else {
      await addToCart();
    }
  } catch {
    // Dociągnięcie się nie powiodło — nie blokuj zakupu, dodaj bieżący produkt.
    await addToCart();
  } finally {
    isResolvingSizes.value = false;
  }
}

async function onSizeSelected(variant: SizeVariant) {
  const id = typeof variant.id === "number" ? variant.id : Number(variant.id);
  if (!Number.isInteger(id) || id <= 0) return;
  try {
    await addToCartFlow.add({
      productId: id,
      productAttributeId: 0,
      quantity: 1,
    });
    if (addToCartFlow.stockError.value) {
      notifications.error(addToCartFlow.stockError.value);
    } else {
      analytics.trackAddToCart(props.product, 1, {
        itemListId: props.itemListId,
        itemListName: props.itemListName,
        index: props.index,
      });
    }
  } catch {
    // errors handled by addToCartFlow notifications
  } finally {
    isSizePickerOpen.value = false;
  }
}

function onProductLinkClick(event: MouseEvent, to: string) {
  analytics.trackSelectItem(props.product, {
    itemListId: props.itemListId,
    itemListName: props.itemListName,
    index: props.index,
  });
  instantProductNavigation.beginProductNavigation(event, to);
}
</script>

<template>
  <article class="product-card" :class="{ 'is-loading': loading }">
    <!-- Skeleton -->
    <template v-if="loading">
      <div class="product-card__skeleton-img" />
      <div class="product-card__info">
        <div class="product-card__skeleton-line" />
        <div
          class="product-card__skeleton-line product-card__skeleton-line--short"
        />
        <div
          class="product-card__skeleton-line product-card__skeleton-line--med"
        />
      </div>
    </template>

    <template v-else>
      <!-- Image area -->
      <div ref="imageRoot" class="product-card__img-wrap">
        <NuxtLink
          :to="cardLink"
          no-prefetch
          class="product-card__img-link"
          @click="onProductLinkClick($event, cardLink)"
        >
          <template v-if="shouldLoadImages">
            <ProductImage
              v-img-fade
              :src="product.thumbnail"
              :alt="product.name"
              width="600"
              height="800"
              :sizes="resolvedImageSizes"
              format="webp"
              :quality="CARD_IMAGE_QUALITY"
              :loading="imageLoading"
              decoding="async"
              :fetchpriority="imageFetchPriority"
              class="product-card__img"
            />
            <NuxtImg
              v-if="product.gallery?.[1]"
              :src="product.gallery[1]"
              :alt="product.name"
              width="600"
              height="800"
              :sizes="resolvedImageSizes"
              format="webp"
              :quality="CARD_IMAGE_QUALITY"
              loading="lazy"
              decoding="async"
              fetchpriority="low"
              class="product-card__img product-card__img--alt"
            />
          </template>
          <div
            v-else
            class="product-card__img-placeholder"
            aria-hidden="true"
          />

          <!-- Badges top-left -->
          <div v-if="isNew || discountPercent" class="product-card__badges">
            <span v-if="isNew" class="badge-new">Nowość</span>
            <span v-if="discountPercent" class="badge-sale">{{
              discountPercent
            }}</span>
          </div>
        </NuxtLink>

        <ClientOnly>
          <ProductCardActions
            class="product-card__overlay-actions"
            :product="product"
            :always-visible="imageOnly === true"
          />
        </ClientOnly>

        <button
          v-if="!imageOnly"
          type="button"
          class="product-card__mobile-quickview"
          aria-label="Szybki podgląd"
          @click.stop.prevent="quickView.open(quickViewSlug)"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        </button>

        <!-- Bottom hover bar -->
        <div v-if="!imageOnly" class="product-card__hover-bar">
          <div class="product-card__bar-meta">
            <p class="product-card__bar-name">{{ product.name }}</p>
            <p
              class="product-card__bar-price"
              :class="{ 'product-card__bar-price--tax': priceTaxNote }"
            >
              <span class="product-card__price-value">{{
                formatPrice(product.price)
              }}</span>
              <span v-if="priceTaxNote" class="product-card__price-tax">{{
                priceTaxNote
              }}</span>
              <span
                v-if="product.compareAtPrice"
                class="product-card__bar-compare"
                >{{ formatPrice(product.compareAtPrice) }}</span
              >
            </p>
            <p
              v-if="product.sizes?.length && !product.sizeVariants?.length"
              class="product-card__bar-size"
            >
              {{ product.sizes.map((s) => s.label).join(" / ") }}
            </p>
            <div
              v-if="product.sizeVariants?.length"
              class="product-card__bar-sizes-chips"
            >
              <NuxtLink
                v-for="v in product.sizeVariants"
                :key="v.id"
                :to="`/products/${v.id}-${v.slug}`"
                no-prefetch
                class="product-card__size-chip"
                :class="{ 'is-current': v.isCurrent }"
                @click.stop="
                  onProductLinkClick($event, `/products/${v.id}-${v.slug}`)
                "
              >
                {{ v.rozmiar }}
              </NuxtLink>
            </div>
          </div>
          <div class="product-card__bar-actions">
            <button
              v-if="sellingEnabled"
              class="product-card__bar-cart-btn"
              type="button"
              :disabled="
                addToCartFlow.isPending.value ||
                stockBlocked ||
                isResolvingSizes
              "
              @click.stop.prevent="onAddToCartClick"
            >
              Dodaj do koszyka
            </button>
            <!-- Przycisk, nie NuxtLink: `@click.prevent` nie zatrzymuje
                 nawigacji NuxtLinka, więc modal wyceny ginął razem
                 z przejściem na /contact. -->
            <button
              v-else
              type="button"
              class="product-card__bar-cart-btn"
              @click.stop="openQuote({ idProduct: resolvedProductId, name: product.name })"
            >
              {{ ctaLabel }}
            </button>
            <button
              type="button"
              class="product-card__bar-view-btn"
              aria-label="Szybki podgląd"
              @click.stop.prevent="quickView.open(quickViewSlug)"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- Info strip -->
      <div v-if="!imageOnly" class="product-card__info">
        <NuxtLink
          v-if="brandLink"
          :to="brandLink"
          no-prefetch
          class="product-card__brand"
          :aria-label="`Zobacz wszystkie produkty marki ${product.brand}`"
          @click.stop
        >
          {{ product.brand }}
        </NuxtLink>
        <p v-else-if="product.brand" class="product-card__brand">
          {{ product.brand }}
        </p>
        <div class="product-card__info-row">
          <NuxtLink
            :to="cardLink"
            no-prefetch
            class="product-card__name"
            @click="onProductLinkClick($event, cardLink)"
          >
            {{ product.name }}
          </NuxtLink>
        </div>
        <p class="product-card__price">
          <span class="product-card__price-value">{{
            formatPrice(product.price)
          }}</span>
          <span v-if="priceTaxNote" class="product-card__price-tax">{{
            priceTaxNote
          }}</span>
          <span v-if="product.compareAtPrice" class="product-card__compare">
            {{ formatPrice(product.compareAtPrice) }}
          </span>
        </p>
        <p class="product-card__sizes">
          {{ product.sizes.map((s) => s.label).join(" / ") }}
        </p>
        <div
          v-if="product.sizeVariants?.length"
          class="product-card__sizes-chips"
        >
          <NuxtLink
            v-for="v in product.sizeVariants"
            :key="v.id"
            :to="`/products/${v.id}-${v.slug}`"
            no-prefetch
            class="product-card__size-chip"
            :class="{ 'is-current': v.isCurrent }"
            @click.stop="
              onProductLinkClick($event, `/products/${v.id}-${v.slug}`)
            "
          >
            {{ v.rozmiar }}
          </NuxtLink>
        </div>
        <button
          v-if="sellingEnabled"
          class="product-card__info-cart-btn"
          type="button"
          :disabled="
            addToCartFlow.isPending.value || stockBlocked || isResolvingSizes
          "
          :aria-label="
            stockBlocked
              ? 'Więcej nie mamy — to ostatnie sztuki'
              : 'Dodaj do koszyka'
          "
          :title="
            stockBlocked
              ? 'Więcej nie mamy — to ostatnie sztuki'
              : 'Dodaj do koszyka'
          "
          @click.stop.prevent="onAddToCartClick"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            aria-hidden="true"
          >
            <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
            <line x1="3" y1="6" x2="21" y2="6" />
            <path d="M16 10a4 4 0 0 1-8 0" />
          </svg>
        </button>
        <button
          v-else
          type="button"
          class="product-card__info-cart-btn"
          :aria-label="ctaLabel"
          :title="ctaLabel"
          @click.stop="openQuote({ idProduct: resolvedProductId, name: product.name })"
        >
          <!-- Koperta, nie koszyk: ikona koszyka w katalogu bez sprzedaży
               obiecywałaby coś, czego nie ma. -->
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            aria-hidden="true"
          >
            <rect x="2" y="4" width="20" height="16" rx="2" />
            <path d="m22 7-10 6L2 7" />
          </svg>
        </button>
      </div>

      <ProductSizePickerModal
        v-if="!imageOnly"
        :open="isSizePickerOpen"
        :variants="pickerVariants"
        :product-name="product.name"
        :pending="addToCartFlow.isPending.value"
        @close="isSizePickerOpen = false"
        @select="onSizeSelected"
      />
    </template>
  </article>
</template>

<style scoped>
.product-card {
  position: relative;
  background: var(--color-white);
  display: flex;
  flex-direction: column;
  min-width: 0;
}

/* ── Image area ── */
.product-card__img-wrap {
  position: relative;
  display: block;
  width: 100%;
  min-width: 0;
  overflow: hidden;
}

.product-card__img-link {
  position: relative;
  display: block;
  width: 100%;
  /* Proporcja i kadrowanie z tokenów marki (themes.css): odzież 3:4 + cover,
     proles kwadrat + contain (całe zdjęcie gadżetu). */
  aspect-ratio: var(--product-card-img-ratio);
  overflow: hidden;
  /* Tło pod placeholderem i pod pasami contain (proles). */
  background: var(--color-white);
}

.product-card__img {
  width: 100%;
  max-width: 100%;
  height: 100%;
  /* cover (odzież) — zdjęcie wypełnia kafelek bez białych ramek, center top
     zachowuje twarz/górę kadru przy delikatnym przycięciu (źródła ~0.75).
     contain (proles) — całe zdjęcie, wyśrodkowane. */
  object-fit: var(--product-img-fit);
  object-position: var(--product-img-position);
  display: block;
  transition: transform 0.4s ease;
}

.product-card__img-placeholder {
  width: 100%;
  height: 100%;
  background: var(--color-bg);
}

.product-card__img--alt {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  transition:
    opacity 0.4s ease,
    transform 0.4s ease;
  /* Kształt brandu (#2): LAVMAG 0, youlook 15px — spójnie z .product-card__img
     (ProductImage.vue), żeby zdjęcie hover nie było kwadratowe pod zaokrąglonym. */
  border-radius: var(--product-img-radius);
  /* Przy contain (proles) pasy obok zdjęcia muszą być kryjące — inaczej przez
     nie prześwituje pierwsze zdjęcie. Odzież: transparent, jak dotąd. */
  background: var(--product-img-letterbox-bg);
}

/* Efekty hover (podmiana zdjęcia na galeryjne + zbliżenie) tylko na urządzeniach
   z prawdziwym wskaźnikiem. Na dotyku (hover: none) brak stanu hover oznacza, że
   pierwszy tap nawiguje od razu — bez „sticky hover" wymuszającego drugi tap. */
@media (hover: hover) {
  .product-card__img-wrap:hover .product-card__img {
    transform: scale(1.02);
  }

  .product-card__img-wrap:hover .product-card__img--alt {
    opacity: 1;
  }
}

/* Badges */
.product-card__badges {
  position: absolute;
  top: 12px;
  left: 12px;
  display: flex;
  gap: 5px;
  z-index: 1;
}

/* Karty w trybie kolumnowym (≤1199px) są wąskie (2 kolumny) — dwa poziome badge
   nachodziły na przycisk ulubionych w prawym górnym rogu, więc układamy je pionowo. */
@media (max-width: 1199px) {
  .product-card__badges {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  /* .badge-sale ma globalnie align-self: flex-end — w kolumnie wypchnęłoby
     badge w prawo; przywracamy wyrównanie do lewej. */
  .product-card__badges .badge-sale {
    align-self: flex-start;
  }
}

/* Overlay action buttons */
.product-card__overlay-actions {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 3;
  opacity: 1;
  transition: opacity var(--transition-fast);
}

.product-card__img-wrap:hover .product-card__overlay-actions {
  opacity: 1;
}

/* ── Bottom bar (always visible, inset 10px) ──
   Pozycja z tokena marki: odzież — nakładka na dół zdjęcia; proles — static,
   pod zdjęciem (offsety nie działają, wcięcie daje margin z tokena). */
.product-card__hover-bar {
  position: var(--product-card-bar-position);
  bottom: 10px;
  left: 10px;
  right: 10px;
  margin: var(--product-card-bar-margin);
  background: var(--color-bg);
  color: var(--color-black);
  padding: 10px 12px;
  z-index: 2;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
}

.product-card__bar-meta {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

/* Do 1199px karta używa prezentacji kolumnowej (info strip pod zdjęciem) —
   ukrywamy desktopowy pasek (na zdjęciu albo pod nim, zależnie od marki). */
@media (max-width: 1199px) {
  .product-card__bar-meta {
    display: none;
  }

  .product-card__hover-bar {
    display: none;
  }
}

.product-card__bar-name {
  margin: 0;
  font-size: 0.8125rem;
  font-weight: var(--font-weight-light);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
  text-transform: uppercase;
}

.product-card__bar-price {
  margin: 0;
  font-size: 0.875rem;
  font-weight: var(--font-weight-normal);
  line-height: 1.3;
  display: flex;
  gap: 6px;
  align-items: baseline;
}

.product-card__bar-compare {
  font-size: 0.75rem;
  color: var(--color-muted);
  text-decoration: line-through;
}

/* Pasek kafla (≥1200px) dzieli wiersz z przyciskiem zapytania — dopisek
   „+ VAT 23%" w jednej linii z ceną chował się pod przyciskiem („5,10 zł + V…").
   Klasa tylko przy dopisku, więc kafle bez niego (lavmag, youlook) bez zmian. */
.product-card__bar-price--tax {
  flex-wrap: wrap;
  row-gap: 0;
}

.product-card__bar-price--tax .product-card__price-tax {
  flex-basis: 100%;
}

/* Katalog (proles, spotkanie 30.10.2026): „za duże zdjęcia, za mała czcionka".
   Listingi mają 4 kafle w rzędzie (od 03.11.2026), więc obok „Zapytaj o wycenę" nazwa
   zostawała z kilkudziesięcioma pikselami („GŁOŚNIK BEZPRZEWODO…"). Nazwa idzie
   na pełną szerokość (do 2 linii), pod nią cena z „+ VAT" i przyciski; czcionki
   13→15 px (nazwa) i 14→16 px (cena). Tylko ≥1200 px — niżej pasek jest ukryty
   (karta pokazuje pasek informacji pod zdjęciem). */
@media (min-width: 1200px) {
  [data-brand="proles"] .product-card__hover-bar {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: end;
    column-gap: 10px;
    row-gap: 6px;
    container: proles-card-bar / inline-size;
  }

  [data-brand="proles"] .product-card__bar-meta {
    display: contents;
  }

  [data-brand="proles"] .product-card__bar-name {
    grid-column: 1 / -1;
    font-size: 0.9375rem;
    white-space: normal;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    /* Zawsze dwie linie — kafle w rzędzie mają równe paski. */
    min-height: 2.6em;
  }

  [data-brand="proles"] .product-card__bar-price {
    font-size: 1rem;
  }

  [data-brand="proles"] .product-card__bar-price .product-card__price-tax {
    font-size: 0.8125rem;
  }

  /* Cena w lewej kolumnie, przyciski w prawej, w wierszu ceny; ewentualne
     rozmiary we własnym wierszu na pełną szerokość (w lewej kolumnie ~100 px
     łamałyby się) — bez jawnego miejsca rozmiar wskoczyłby obok ceny. */
  [data-brand="proles"] .product-card__bar-price {
    grid-column: 1;
  }

  [data-brand="proles"] .product-card__bar-size,
  [data-brand="proles"] .product-card__bar-sizes-chips {
    grid-column: 1 / -1;
  }

  [data-brand="proles"] .product-card__bar-actions {
    grid-column: 2;
    grid-row: 2;
  }
}

/* Wąski kafel: cena z „+ VAT" (~70 px) i przyciski (~185 px) nie mieszczą się
   w jednym wierszu — przycisk wchodził na cenę. Przyciski schodzą wtedy do
   własnego wiersza na pełną szerokość. Kontener = treść paska bez paddingu
   2×12 px: ≤ 279 px to listingi (z panelem filtrów) do 1824 px i siatki na
   pełną szerokość (home, karta produktu) do 1504 px. nowrap: etykieta ma
   108,8 px, a przy 1201 px zostaje jej 110 px — inny krój (zanim dojdzie
   Funnel Sans) łamałby ją na dwie linie w przycisku 40 px. */
@container proles-card-bar (max-width: 279px) {
  [data-brand="proles"] .product-card__bar-actions {
    grid-column: 1 / -1;
    grid-row: auto;
  }

  [data-brand="proles"] .product-card__bar-cart-btn {
    flex: 1;
    white-space: nowrap;
  }
}

/* Bardzo wąski kafel (listingi do ok. 1464 px; pełna szerokość tu nie schodzi):
   etykieta 108,8 + padding 2×14 + ramka 2 + odstęp 8 + lupa 40 = ~187 px, więc
   poniżej 190 px treści paska lupa (szybki podgląd) znika — kafel i tak
   prowadzi do karty produktu — a przycisk zapytania zajmuje cały wiersz
   z mniejszym wcięciem. */
@container proles-card-bar (max-width: 189px) {
  [data-brand="proles"] .product-card__bar-view-btn {
    display: none;
  }

  [data-brand="proles"] .product-card__bar-cart-btn {
    padding: 0 10px;
  }
}

.product-card__bar-size {
  margin: 0;
  font-size: 0.75rem;
  color: #707070;
  line-height: 1.3;
}

.product-card__bar-sizes-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.product-card__bar-actions {
  display: flex;
  gap: 8px;
  align-items: stretch;
}

.product-card__bar-cart-btn {
  flex-shrink: 0;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--btn-primary-bg, var(--color-black));
  color: var(--btn-primary-text, var(--color-white));
  border: 1px solid var(--btn-primary-bg, var(--color-black));
  border-radius: var(--btn-pill-radius);
  padding: 0 14px;
  font-family: var(--font-body);
  font-size: 0.8125rem;
  font-weight: var(--font-weight-normal);
  letter-spacing: 0.04em;
  cursor: pointer;
  transition:
    background var(--transition-fast),
    color var(--transition-fast),
    border-color var(--transition-fast);
}

.product-card__bar-cart-btn:hover {
  background: var(--color-white);
  color: var(--color-black);
}

.product-card__bar-cart-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.product-card__bar-view-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  color: var(--btn-primary-text, var(--color-white));
  border: 1px solid var(--color-white);
  border-radius: var(--btn-pill-radius);
  transition: background var(--transition-fast);
  background: var(--btn-primary-bg, var(--color-black));
}

.product-card__bar-view-btn:hover {
  background: var(--color-border);
  color: var(--color-black);
  border-color: var(--color-black);
  cursor: pointer;
}

/* ── Mobile quickview button ── */
.product-card__mobile-quickview {
  display: none;
}

@media (max-width: 1199px) {
  .product-card__mobile-quickview {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    bottom: 10px;
    right: 10px;
    width: 36px;
    height: 36px;
    background: var(--btn-primary-bg, var(--color-black));
    color: var(--btn-primary-text, var(--color-white));
    border-radius: var(--btn-pill-radius);
    z-index: 2;
    transition: background var(--transition-fast);
  }

  .product-card__mobile-quickview:hover {
    background: var(--color-border);
    color: var(--color-black);
  }
}

/* ── Info strip ── */
.product-card__info {
  background: var(--color-bg);
  padding: 13px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.product-card__info-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.product-card__brand {
  margin: 0;
  align-self: flex-start;
  font-size: 0.6875rem;
  font-weight: var(--font-weight-normal);
  color: var(--color-muted);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  line-height: 1;
  text-decoration: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

a.product-card__brand:hover {
  color: var(--color-black);
  text-decoration: underline;
}

.product-card__name {
  font-size: 1rem;
  font-weight: var(--font-weight-light);
  color: var(--color-black);
  line-height: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-transform: uppercase;
}

.product-card__price {
  margin: 0;
  font-size: 1rem;
  font-weight: var(--font-weight-light);
  color: var(--color-black);
  line-height: 1;
  display: flex;
  gap: 8px;
  align-items: baseline;
}

/* Cena sprzedaży wyróżniona prostokątem z zaokrąglonymi rogami (prośba lavmaga,
   16.09.2026 — kolor ten sam co belka promocyjna u góry strony). Geometria i kolor
   siedzą w tokenach markowych: poza lavmagiem wszystkie są wyzerowane, więc kafel
   wygląda dokładnie jak dotąd. Przekreślona cena regularna celowo zostaje poza
   chipem — tło ma podkreślać tylko to, ile klient płaci.

   Klasa jest wspólna dla OBU prezentacji kafla, bo listing pokazuje cenę w innym
   miejscu zależnie od szerokości: ≥1200px działa pasek kafla
   (.product-card__bar-price), a .product-card__info ma display:none; ≤1199px jest
   odwrotnie. Wyróżnienie tylko w jednym z nich zniknęłoby na połowie urządzeń. */
.product-card__price-value {
  background: var(--price-chip-bg, transparent);
  color: var(--price-chip-text, inherit);
  border-radius: var(--price-chip-radius, 0);
  padding: var(--price-chip-pad, 0);
  white-space: nowrap;
}

.product-card__compare {
  font-size: 0.875rem;
  color: var(--color-muted);
  text-decoration: line-through;
}

/* Dopisek „+ VAT 23%" (BrandConfig.priceTaxNote) — dopisek, nie druga cena. */
.product-card__price-tax {
  font-size: 0.75rem;
  color: var(--color-muted);
  white-space: nowrap;
}

.product-card__sizes {
  margin: 0;
  font-size: 0.75rem;
  font-weight: var(--font-weight-light);
  color: #707070;
  line-height: 1.3;
}

/* ── Skeleton ── */
@keyframes skeleton-shimmer {
  0% {
    background-position: 120% 0;
  }
  100% {
    background-position: -120% 0;
  }
}

.product-card.is-loading {
  background: var(--color-bg);
}

.product-card__skeleton-img,
.product-card__skeleton-line {
  position: relative;
  overflow: hidden;
  background: linear-gradient(
    100deg,
    var(--color-bg) 0%,
    var(--color-border) 38%,
    var(--color-white) 50%,
    var(--color-border) 62%,
    var(--color-bg) 100%
  );
  background-size: 220% 100%;
  animation: skeleton-shimmer 1.35s ease-in-out infinite;
}

.product-card__skeleton-img {
  width: 100%;
  aspect-ratio: var(--product-card-img-ratio);
  border: 1px solid var(--color-border);
}

.product-card__skeleton-line {
  height: 14px;
  border-radius: 999px;
  width: 100%;
}

.product-card__skeleton-line--short {
  width: 45%;
}

.product-card__skeleton-line--med {
  width: 65%;
}

.product-card__sizes-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.product-card__size-chip {
  font-size: 0.75rem;
  font-weight: var(--font-weight-light);
  color: var(--color-muted);
  background: transparent;
  padding: 2px 0;
  text-decoration: none;
  line-height: 1;
  white-space: nowrap;
}

.product-card__size-chip:hover {
  text-decoration: underline;
  color: var(--color-black);
}

.product-card__info-cart-btn {
  width: 100%;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--btn-primary-bg, var(--color-black));
  color: var(--btn-primary-text, var(--color-white));
  border: 1px solid var(--btn-primary-bg, var(--color-black));
  border-radius: var(--btn-pill-radius);
  font-family: var(--font-body);
  font-size: 0.875rem;
  font-weight: var(--font-weight-normal);
  letter-spacing: 0.04em;
  cursor: pointer;
  transition:
    background var(--transition-fast),
    color var(--transition-fast);
}

.product-card__info-cart-btn:hover {
  background: var(--color-white);
  color: var(--color-black);
}

.product-card__info-cart-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (min-width: 1200px) {
  .product-card__info {
    display: none;
  }

  /* Info szkieletu też znika, a u proles pasek stoi pod zdjęciem (nie na nim)
     — bez rezerwy każdy rząd rósłby po załadowaniu. Odzież: 0. */
  .product-card__skeleton-img {
    margin-bottom: var(--product-card-bar-reserve);
  }
}
</style>
